// Service interfaces and types for standardized service architecture

import {
  ServiceResult as BaseServiceResult,
  ServiceError as BaseServiceError,
} from "./BaseService";

/**
 * Common pagination parameters used across services
 */
export interface PaginationParams {
  skip?: number;
  limit?: number;
  offset?: number;
  page?: number;
}

/**
 * Validation context for error messages
 */
export interface ValidationContext {
  operation: string;
  entity: string;
  field?: string;
}

/**
 * Service operation context for logging and error handling
 */
export interface ServiceOperationContext {
  operation: string;
  entity: string;
  entityId?: string;
  userId?: string;
  metadata?: Record<string, unknown>;
}

/**
 * error interface with additional context
 */
export interface ServiceError extends BaseServiceError {
  context?: ServiceOperationContext;
  validationErrors?: Array<{
    field: string;
    message: string;
    code?: string;
  }>;
  httpStatus?: number;
}

/**
 * service result with better error context
 */
export interface ServiceResult<T> extends BaseServiceResult<T> {
  error?: BaseServiceError;
}

/**
 * Base interface for all domain services
 */
export interface IDomainService {
  readonly serviceName: string;
}

/**
 * Interface for services that handle entity operations
 */
export interface IEntityService<T, TCreate, TUpdate> extends IDomainService {
  create(data: TCreate): Promise<ServiceResult<T>>;
  getById(id: string): Promise<ServiceResult<T>>;
  update(id: string, data: TUpdate): Promise<ServiceResult<T>>;
  delete(id: string): Promise<ServiceResult<void>>;
}

/**
 * Interface for services that handle list operations
 */
export interface IListService<T> extends IDomainService {
  list(params?: PaginationParams): Promise<ServiceResult<T[]>>;
}

/**
 * Interface for services that handle paginated list operations
 */
export interface IPaginatedListService<T> extends IDomainService {
  list(params?: PaginationParams): Promise<
    ServiceResult<{
      items: T[];
      total: number;
      limit: number;
      offset: number;
    }>
  >;
}

/**
 * Service configuration options
 */
export interface ServiceConfig {
  retries?: number;
  timeout?: number;
  enableLogging?: boolean;
  enableValidation?: boolean;
}

/**
 * Default service configuration
 */
export const DEFAULT_SERVICE_CONFIG: ServiceConfig = {
  retries: 3,
  timeout: 30000,
  enableLogging: true,
  enableValidation: true,
};
